import React, { useCallback, useMemo, useState } from 'react';
import {
  Paper, Stack, Typography, Chip, Button, Skeleton, Box, FormControlLabel, Switch
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Label
} from 'recharts';
import MonthPicker from '../reports/MonthPicker';
import { useMonthlyRollups, useBudgetVsActual, useSavingsTrajectory } from '../../hooks/useReports';
import { MonthlyRollup } from '../../types/ReportsTrends';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';

// helper: month -> UTC range (YYYY-MM-DD)
const monthRange = (yyyyMM: string) => {
  const [y, m] = yyyyMM.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, 1));
  const end = new Date(Date.UTC(y, m, 0, 23, 59, 59, 999)); // last day of month
  const iso = (d: Date) => d.toISOString().split('T')[0];
  return { startDate: iso(start), endDate: iso(end) };
};

const buildReportsUrl = (month: string, includeTentative: boolean) => {
  const qs = new URLSearchParams({
    month,
    includeTentative: String(includeTentative),
  }).toString();
  return `/reports?${qs}`;
};

const ReportsPreview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const formatting = useFormatting();
  const fmtCurrency = useCallback(
    (v?: number) =>
      typeof v === 'number'
        ? formatMoney(v, formatting, { maximumFractionDigits: 0 })
        : '–',
    [formatting]
  );

  const [month, setMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
  const [includeTentative, setIncludeTentative] = useState(false);

  const params = useMemo(() => ({ month, includeTentative, showFuture: false }), [month, includeTentative]);
  const { data: rollups, isLoading: loadingRollups } = useMonthlyRollups(params);
  const { data: bva } = useBudgetVsActual(params);
  const { data: savings } = useSavingsTrajectory(params);

  const current: MonthlyRollup | undefined = rollups?.find(d => d.month === month);
  const budget = bva?.series.find(s => s.month === month);

  // ---- Drillthrough handlers ----
  const goToTransactions = (type: 'income'|'expense'|'savings') => {
    if (!month) return;
    const { startDate, endDate } = monthRange(month);
    const qs = new URLSearchParams({
      type,
      startDate,
      endDate,
      // keep app semantics:
      showFuture: 'false',
    }).toString();
    navigate(`/transactions?${qs}`);
  };

  const onBarClick = (dataKey: string) => {
    // Map clicked series -> tx type
    if (dataKey === 'income') goToTransactions('income');
    if (dataKey === 'expense') goToTransactions('expense');
    if (dataKey === 'savings') goToTransactions('savings');
  };

  const onNetClick = () => {
    // Net is composite; send to full reports for context
    navigate(buildReportsUrl(month, includeTentative));
  };

  const onBudgetClick = () => {
    navigate('/budgets');
  };

  // quick alerts (lightweight rules)
  const alerts = useMemo(() => {
    const list: string[] = [];
    if (!current) return list;

    if (budget) {
      const over = budget.variance < 0;
      const delta = Math.abs(budget.variance);
      if (over) list.push(`Over budget by ${fmtCurrency(delta)}.`);
      else if (delta > 0) list.push(`On track: ${fmtCurrency(delta)} under budget.`);
      if ((budget.overBudgetCategoryCount || 0) > 0) {
        list.push(`${budget.overBudgetCategoryCount} category budget(s) over.`);
      }
    }
    // low savings rate
    const sr = current.income > 0 ? current.savings / current.income : 0;
    if (sr < 0.1) list.push('Savings rate under 10%. Consider adjusting.');
    // negative/low net
    if (current.net < 0) list.push('Negative net this month.');
    return list.slice(0, 3);
  }, [current, budget, fmtCurrency]);

  // chart rows
  const rows = useMemo(() => {
    if (!current) return [];
    return [{
      label: 'This Month',
      income: current.income,
      expense: current.expense,
      savings: current.savings,
      net: current.net,
      budgeted: budget?.budgeted,
    }];
  }, [current, budget]);

  const budgetY = rows[0]?.budgeted;
  const maxY = Math.max(
    1,
    ...(rows.length ? [rows[0].income, rows[0].expense, rows[0].savings, budgetY ?? 0] : [1])
  );
  const yDomain: [number, number] = [0, Math.ceil(maxY * 1.15)];

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2}>
        <Typography variant="h6" fontWeight="bold">📊 Reports & Trends</Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <MonthPicker
            value={month}
            onChange={setMonth}
            fullWidth
            sx={{ minWidth: { xs: '100%', sm: 180 }, flex: { sm: '0 0 auto' } }}
          />
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Switch size="small" checked={includeTentative} onChange={(e)=>setIncludeTentative(e.target.checked)} />}
            label="Tentative"
          />
          <Button
            variant="outlined"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
            onClick={() => navigate(buildReportsUrl(month, includeTentative))}
          >
            Open Reports →
          </Button>
        </Stack>
      </Stack>

      {/* Chart */}
      <Box sx={{ mt: 2, height: 220 }}>
        {loadingRollups ? (
          <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
        ) : rows.length === 0 ? (
          <Box sx={{ height: '100%', display:'flex', alignItems:'center', justifyContent:'center', opacity:.7 }}>
            No data for this month.
          </Box>
        ) : (
          <ResponsiveContainer>
            <ComposedChart
              data={rows}
              barCategoryGap={28}
              margin={{ top: 6, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis domain={yDomain} tickFormatter={(n:number)=>fmtCurrency(n)} />
              <Tooltip
                formatter={(val:any, name:any) => [fmtCurrency(val), name]}
                labelFormatter={() => month}
              />

              {/* Bars with click-through */}
              <Bar
                dataKey="income"
                name="Income"
                fill={theme.palette.success.main}
                radius={[6,6,0,0]}
                onClick={() => onBarClick('income')}
                cursor="pointer"
              />
              <Bar
                dataKey="expense"
                name="Expenses"
                fill={theme.palette.error.main}
                radius={[6,6,0,0]}
                onClick={() => onBarClick('expense')}
                cursor="pointer"
              />
              <Bar
                dataKey="savings"
                name="Transfers"
                fill={theme.palette.info.main}
                radius={[6,6,0,0]}
              />

              {/* Net line -> Reports deep dive */}
              <Line
                type="monotone"
                dataKey="net"
                name="Net"
                stroke={theme.palette.text.primary}
                strokeWidth={2}
                dot
                onClick={onNetClick}
                cursor="pointer"
              />

              {/* Budget line -> Budgets page */}
              {typeof budgetY === 'number' && budgetY > 0 && (
                <ReferenceLine
                  y={budgetY}
                  stroke={theme.palette.warning.main}
                  strokeDasharray="6 4"
                  ifOverflow="extendDomain"
                  onClick={onBudgetClick}
                  cursor="pointer"
                >
                  <Label position="right" value={`Budget ${fmtCurrency(budgetY)}`} />
                </ReferenceLine>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Box>


      {/* Alerts */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
        {alerts.length ? alerts.map((t, i) => <Chip key={i} label={t} color={/Over|Negative/.test(t) ? 'error' : 'default'} />)
        : <Typography variant="body2" color="text.secondary">No alerts for this month.</Typography>}
      </Stack>

      {/* Savings footnote */}
      {savings?.savings && (
        <Typography variant="caption" sx={{ mt: 1, display:'block', opacity:.75 }}>
          Savings this period: {fmtCurrency(savings.savings.inRange)} · Cumulative savings: {fmtCurrency(savings.savings.cumulative)}
        </Typography>
      )}
    </Paper>
  );
};

export default ReportsPreview;
